<script>
    import onboardingService from '@/services/onboarding.service';
    import Spinner from '@/components/shared/Spinner';
    import ButtonSpinner from '@/components/shared/ButtonSpinner';
    import PlanShell from '@/components/onboarding/PlanShell';
    import WorkbookNotices from '@/components/onboarding/WorkbookNotices';

    const JOB_KINDS = [
        { value: 5, label: 'BatteryImport' },
        { value: 1, label: 'Import' },
        { value: 6, label: 'FieldDirectImport' }
    ];

    export default {
        components: { Spinner, ButtonSpinner, PlanShell, WorkbookNotices },
        data() {
            return {
                session: null,
                isLoading: true,
                error: null,
                busy: null,
                jobKinds: JOB_KINDS,
                recapNotes: '',
                recapSavedState: 'idle',
                recapSaveTimer: null,
                recapSavedNotes: '',
                isRecapSaving: false,
                isMounted: true,
                activeTab: 'preview',
                preview: null,
                previewError: null,
                isPreviewLoading: false,
                collapsedFacilities: {},
                buildPlan: null,
                buildPlanError: null,
                isBuildPlanLoading: false,
                approveOutcome: null,
                feedback: '',
                messages: [],
                planQuery: '',
                planStructure: 'shell',
                typeformResponses: null,
                typeformResponseId: ''
            };
        },
        computed: {
            sessionId() {
                return this.$route.params.id;
            },
            files() {
                return this.session?.files || [];
            },
            maxFiles() {
                return this.session?.maxFiles || 10;
            },
            canAddFile() {
                return this.files.length < this.maxFiles;
            },
            previewFacilities() {
                return this.preview?.facilities || [];
            },
            previewCounts() {
                return this.preview?.counts || {};
            },
            previewSections() {
                return this.preview?.sections || [];
            },
            previewNotes() {
                const fileNotes = this.preview?.extractionNotes || [];
                const sectionNotes = (this.preview?.sections || []).flatMap(s => s.notes || []);
                return [...fileNotes, ...sectionNotes];
            },
            previewErrors() {
                return (this.preview?.validations || []).filter(v => v.severity === 'error');
            },
            previewWarnings() {
                return (this.preview?.validations || []).filter(v => v.severity === 'warning');
            },
            plan() {
                return this.buildPlan?.plan || null;
            },
            planFacilities() {
                const plan = this.plan;
                if (!plan) return [];
                const under = (items, name) => items.filter(i => i.facility === name);
                return plan.facilities.map(f => ({
                    ...f,
                    wells: under(plan.wells, f.name),
                    tanks: under(plan.tanks, f.name),
                    meters: under(plan.meters, f.name)
                }));
            },
            planSearch() {
                return this.planQuery.trim().toLowerCase();
            },
            shownFacilities() {
                const q = this.planSearch;
                if (!q) return this.planFacilities;
                const hit = item => [item.name, item.apiNumber].some(v => v && String(v).toLowerCase().includes(q));
                return this.planFacilities
                    .map(f => (hit(f) || (f.collectionPoint || '').toLowerCase().includes(q)
                        ? f
                        : { ...f, wells: f.wells.filter(hit), tanks: f.tanks.filter(hit), meters: f.meters.filter(hit) }))
                    .filter(f => hit(f) || f.wells.length || f.tanks.length || f.meters.length);
            },
            shownPeople() {
                const q = this.planSearch;
                const people = this.plan?.people || [];
                if (!q) return people;
                return people.filter(p => [p.name, p.email, p.role, ...p.facilities].some(v => v && v.toLowerCase().includes(q)));
            },
            shownGroups() {
                const q = this.planSearch;
                const groups = this.plan?.groups || [];
                if (!q) return groups;
                return groups.filter(g => [g.name, ...g.facilities, ...g.members].some(v => v && v.toLowerCase().includes(q)));
            },
            operatorMessages() {
                return this.messages.filter(m => m.sourceName !== 'Assistant').slice(-5);
            },
            openQuestions() {
                const closed = this.plan?.closedQuestions || [];
                return (this.plan?.questions || []).filter(q => !closed.includes(q));
            },
            approveBlockedReason() {
                if (this.buildPlan?.status === 'Queued') return 'A new plan is being written; approve that one';
                if (this.openQuestions.length) return 'Answer or tick off the open questions first';
                return '';
            },
            buildStage() {
                return this.buildPlan?.buildStage || 'NotApproved';
            },
            isBuilding() {
                return this.buildStage === 'Importing' || this.buildStage === 'Finishing';
            },
            buildResult() {
                if (this.buildStage !== 'Built' || !this.buildPlan?.buildLog) return null;
                try {
                    return JSON.parse(this.buildPlan.buildLog);
                } catch {
                    return null;
                }
            },
            wellDbSummary() {
                const withApi = (this.plan?.wells || []).filter(w => w.apiNumber);
                if (!withApi.length) return '';
                return `${withApi.filter(w => w.wellDb).length} of ${withApi.length} API numbers found in the well database`;
            },
            planCounts() {
                const plan = this.plan;
                if (!plan) return [];
                return ['facilities', 'wells', 'tanks', 'meters', 'people', 'groups'].map(kind => {
                    const items = plan[kind] || [];
                    const existing = items.filter(i => i.status === 'Existing').length;
                    return { kind, total: items.length, existing, new: items.length - existing };
                });
            },
            hasExtraction() {
                return !!this.preview?.hasExtraction;
            },
            recapSavedLabel() {
                if (this.recapSavedState === 'saving') return 'Saving…';
                if (this.recapSavedState === 'saved') return 'Saved';
                if (this.recapSavedState === 'error') return 'Not saved — retrying on your next edit';
                return '';
            },
            recapSavedClass() {
                if (this.recapSavedState === 'error') return 'text-danger';
                return 'text-muted';
            },
        },
        beforeUnmount() {
            this.isMounted = false;
            if (this.recapSaveTimer) clearTimeout(this.recapSaveTimer);
        },
        async mounted() {
            await this.load();
            if (this.session) await Promise.all([this.loadPreview(), this.loadBuildPlan()]);
        },
        methods: {
            apiError(err, fallback) {
                return err?.data?.message || err?.message || fallback;
            },
            jobKindLabel(kind) {
                return JOB_KINDS.find(k => k.value === kind)?.label || String(kind);
            },
            applySession(session) {
                this.session = session;
                this.recapNotes = session.recapNotes || '';
                this.recapSavedNotes = this.recapNotes;
            },
            onRecapNotesInput() {
                // Typing is not a save. Wait for a pause so a sentence costs one request, not
                // one per keystroke, and leave the state visible while it settles.
                this.recapSavedState = 'idle';
                if (this.recapSaveTimer) clearTimeout(this.recapSaveTimer);
                this.recapSaveTimer = setTimeout(this.saveRecapNotes, 900);
            },
            flushRecapNotes() {
                // Leaving the field should not wait out the debounce.
                if (this.recapSaveTimer) {
                    clearTimeout(this.recapSaveTimer);
                    this.recapSaveTimer = null;
                }
                this.saveRecapNotes();
            },
            async saveRecapNotes() {
                this.recapSaveTimer = null;
                if (!this.session?.isOpen) return;
                if (this.recapNotes === this.recapSavedNotes) return;
                // One save at a time. Two PUTs in flight can land out of order and let the older
                // text win; whatever is newer is picked up by the re-arm below instead.
                if (this.isRecapSaving) return;

                const pending = this.recapNotes;
                this.isRecapSaving = true;
                this.recapSavedState = 'saving';
                try {
                    const session = await onboardingService.updateSession(this.sessionId, {
                        recapNotes: pending
                    });
                    this.recapSavedNotes = pending;
                    if (this.error) this.error = null;
                    // Do not applySession here: it would overwrite whatever was typed while the
                    // request was in flight. Only take the fields the server owns.
                    if (this.session) {
                        this.session.recapNotes = session.recapNotes;
                        this.session.updatedDate = session.updatedDate;
                    }
                    this.recapSavedState = this.recapNotes === pending ? 'saved' : 'idle';
                    // Anything typed while that request was in flight still has to reach the
                    // server. Only on success: re-arming after a failure would both hide the
                    // error and retry against an endpoint that is already failing.
                    if (this.isMounted && this.recapNotes !== this.recapSavedNotes) {
                        this.isRecapSaving = false;
                        this.onRecapNotesInput();
                    }
                } catch (err) {
                    this.recapSavedState = 'error';
                    this.error = this.apiError(err, 'Could not save recap notes.');
                } finally {
                    this.isRecapSaving = false;
                }
            },
            async closeSession() {
                if (!window.confirm('Close this onboarding session?')) return;
                this.busy = 'close';
                this.error = null;
                try {
                    this.applySession(await onboardingService.close(this.sessionId));
                } catch (err) {
                    this.error = this.apiError(err, 'Could not close session.');
                } finally {
                    this.busy = null;
                }
            },
            async reopenSession() {
                this.busy = 'reopen';
                this.error = null;
                try {
                    this.applySession(await onboardingService.reopen(this.sessionId));
                } catch (err) {
                    this.error = this.apiError(err, 'Could not reopen session.');
                } finally {
                    this.busy = null;
                }
            },
            async load() {
                this.isLoading = true;
                this.error = null;
                try {
                    this.applySession(await onboardingService.get(this.sessionId));
                } catch (err) {
                    this.error = this.apiError(err, 'Could not load session.');
                } finally {
                    this.isLoading = false;
                }
            },
            async onAddFile(e) {
                const picked = Array.from(e.target.files || []);
                if (!picked.length) return;
                const room = this.maxFiles - this.files.length;
                if (picked.length > room) {
                    this.error = `A session can have at most ${this.maxFiles} files; there is room for ${Math.max(room, 0)} more.`;
                    e.target.value = '';
                    return;
                }
                this.busy = 'upload';
                this.error = null;
                try {
                    // One at a time: each upload returns the session, and the server counts the limit per file.
                    for (const file of picked) {
                        this.applySession(await onboardingService.addFile(this.sessionId, file));
                    }
                } catch (err) {
                    this.error = this.apiError(err, 'Upload failed.');
                } finally {
                    this.busy = null;
                    e.target.value = '';
                }
            },
            async loadTypeformResponses() {
                this.busy = 'typeform';
                this.error = null;
                try {
                    this.typeformResponses = (await onboardingService.listTypeformResponses()) || [];
                } catch (err) {
                    this.error = this.apiError(err, 'Could not read the Gear Up form.');
                } finally {
                    this.busy = null;
                }
            },
            async linkTypeformResponse() {
                if (!this.typeformResponseId) return;
                this.busy = 'typeform';
                this.error = null;
                try {
                    this.applySession(await onboardingService.linkTypeformResponse(this.sessionId, this.typeformResponseId));
                    this.typeformResponses = null;
                    this.typeformResponseId = '';
                    await this.loadBuildPlan();
                } catch (err) {
                    this.error = this.apiError(err, 'Could not bring in the Typeform response.');
                } finally {
                    this.busy = null;
                }
            },
            typeformResponseLabel(response) {
                const date = response.submittedAt ? new Date(response.submittedAt).toLocaleDateString() : 'no date';
                const linked = response.sessionId ? ` · gear-up ${response.sessionId}` : '';
                return `${response.companyName || 'No company name'} · ${date}${response.hasLeaseFile ? ' · lease file' : ''}${linked}`;
            },
            statusBadgeClass(status) {
                if (status === 'Existing') return 'bg-secondary';
                if (status === 'Missing') return 'bg-danger';
                return 'bg-success';
            },
            confidenceLabel(value) {
                return `${Math.round((value || 0) * 100)}%`;
            },
            confidenceClass(value) {
                if (value >= 0.8) return 'text-success';
                if (value >= 0.5) return 'text-warning';
                return 'text-danger';
            },
            facilityKey(facility) {
                return facility.name || 'Rows with no facility';
            },
            isFacilityCollapsed(facility) {
                return !!this.collapsedFacilities[this.facilityKey(facility)];
            },
            setAllCollapsed(collapsed) {
                this.collapsedFacilities = Object.fromEntries(this.planFacilities.map(f => [this.facilityKey(f), collapsed]));
            },
            toggleFacility(facility) {
                const key = this.facilityKey(facility);
                this.collapsedFacilities[key] = !this.collapsedFacilities[key];
            },
            async loadPreview() {
                this.isPreviewLoading = true;
                this.previewError = null;
                try {
                    this.preview = await onboardingService.getPreview(this.sessionId);
                } catch (err) {
                    this.previewError = this.apiError(err, 'Could not load preview.');
                } finally {
                    this.isPreviewLoading = false;
                }
            },
            async loadBuildPlan() {
                this.isBuildPlanLoading = true;
                this.buildPlanError = null;
                try {
                    this.buildPlan = await onboardingService.getBuildPlan(this.sessionId);
                    this.messages = (await onboardingService.listMessages(this.sessionId)) || [];
                } catch (err) {
                    this.buildPlanError = this.apiError(err, 'Could not load the build plan.');
                } finally {
                    this.isBuildPlanLoading = false;
                }
            },
            async sendFeedback() {
                const body = this.feedback.trim();
                if (!body) return;
                this.busy = 'feedback';
                this.error = null;
                try {
                    await onboardingService.postMessage(this.sessionId, body);
                    this.feedback = '';
                    await this.loadBuildPlan();
                } catch (err) {
                    this.error = this.apiError(err, 'Could not send the feedback.');
                } finally {
                    this.busy = null;
                }
            },
            async setQuestionClosed(question, closed) {
                this.busy = 'question';
                this.error = null;
                try {
                    this.buildPlan = await onboardingService.setQuestionClosed(this.sessionId, this.buildPlan.planId, question, closed);
                } catch (err) {
                    this.error = this.apiError(err, 'Could not update the question.');
                } finally {
                    this.busy = null;
                }
            },
            async setBookie(person) {
                this.busy = 'bookie';
                this.error = null;
                try {
                    this.buildPlan = await onboardingService.setPlanBookie(this.sessionId, this.buildPlan.planId, person ? person.name : null);
                } catch (err) {
                    this.error = this.apiError(err, 'Could not set the bookie.');
                } finally {
                    this.busy = null;
                }
            },
            async approveBuild() {
                const plan = this.plan;
                if (!plan || !window.confirm(`Build this plan in ${this.session.companyName || 'this company'}? Only what it marks new is created.`)) {
                    return;
                }
                this.busy = 'approve';
                this.error = null;
                this.approveOutcome = null;
                try {
                    this.approveOutcome = await onboardingService.approveBuildPlan(this.sessionId, this.buildPlan.planId);
                    await this.loadBuildPlan();
                } catch (err) {
                    this.error = this.apiError(err, 'Could not approve the plan.');
                } finally {
                    this.busy = null;
                }
            },
            facilityTypeLabel(type) {
                if (type === 'CollectionPoint') return 'CP';
                if (type === 'SWD') return 'SWD';
                return null;
            },
            feet(inches) {
                return inches == null ? '?' : `${Math.round((inches / 12) * 10) / 10} ft`;
            },
            async removeFile(file) {
                if (!window.confirm(`Remove ${file.sourceFileName || ('file ' + file.id)} from this session?`)) {
                    return;
                }
                this.busy = `delete-${file.id}`;
                this.error = null;
                try {
                    this.applySession(await onboardingService.deleteFile(this.sessionId, file.id));
                } catch (err) {
                    this.error = this.apiError(err, 'Remove failed.');
                } finally {
                    this.busy = null;
                }
            },
        },
        watch: {
            plan(plan) {
                if (plan) this.activeTab = 'plan';
            }
        }
    };
</script>

<template>
    <div class="container-fluid py-3">
        <div class="mb-3">
            <router-link to="/onboarding">&larr; Back to list</router-link>
        </div>

        <Spinner v-if="isLoading" />
        <div v-else-if="!session" class="alert alert-danger">{{ error || 'Session not found.' }}</div>
        <template v-else>
            <div class="d-flex flex-wrap justify-content-between gap-2 mb-1">
                <h1 class="h3 mb-0">Onboarding session {{ session.id }}</h1>
                <div class="d-flex gap-2">
                    <button
                        v-if="session.isOpen"
                        class="btn btn-outline-secondary btn-sm"
                        type="button"
                        :disabled="!!busy"
                        @click="closeSession"
                    >
                        <ButtonSpinner v-if="busy === 'close'" />
                        Close session
                    </button>
                    <button
                        v-else
                        class="btn btn-outline-primary btn-sm"
                        type="button"
                        :disabled="!!busy"
                        @click="reopenSession"
                    >
                        <ButtonSpinner v-if="busy === 'reopen'" />
                        Reopen
                    </button>
                </div>
            </div>
            <p class="text-muted mb-3">
                {{ session.companyName || ('Company ' + session.companyId) }}
                · Session stage: <strong>{{ session.stageName }}</strong>
                · Files: {{ files.length }}/{{ maxFiles }}
                <span v-if="!session.isOpen"> · Closed ({{ session.closedReasonName || 'manual' }})</span>
            </p>

            <div v-if="error" class="alert alert-danger">{{ error }}</div>

            <div class="border rounded p-3 mb-4">
                <div class="d-flex justify-content-between align-items-baseline">
                    <h2 class="h5 mb-0">Recap notes</h2>
                    <span class="small" :class="recapSavedClass">{{ recapSavedLabel }}</span>
                </div>
                <p class="small text-muted">Used by the Recap tab (and Sonnet) for Slack-style status bullets.</p>
                <label class="form-label" for="recap-notes">Notes</label>
                <textarea
                    id="recap-notes"
                    v-model="recapNotes"
                    class="form-control"
                    rows="2"
                    :disabled="!session.isOpen"
                    placeholder="e.g. first pumper batch done; water meters adjusted…"
                    @input="onRecapNotesInput"
                    @blur="flushRecapNotes"
                />
            </div>

            <div class="border rounded p-3 mb-4">
                <div class="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
                    <div>
                        <h2 class="h5 mb-0">Files</h2>
                        <p class="small text-muted mb-0">
                            Read automatically, like a workbook dropped in the Slack thread. Max {{ maxFiles }} per session.
                        </p>
                    </div>
                    <div class="d-flex align-items-center gap-2">
                        <label class="btn btn-primary mb-0" :class="{ disabled: !!busy || !canAddFile }">
                            Add files
                            <input
                                class="d-none"
                                type="file"
                                multiple
                                accept=".csv,.xlsx,.xls"
                                :disabled="!!busy || !canAddFile || !session.isOpen"
                                @change="onAddFile"
                            />
                        </label>
                        <button
                            v-if="!typeformResponses"
                            type="button"
                            class="btn btn-outline-primary"
                            :disabled="!!busy || !session.isOpen"
                            @click="loadTypeformResponses"
                        >
                            From Typeform
                        </button>
                        <template v-else>
                            <select v-model="typeformResponseId" class="form-select" aria-label="Gear Up response">
                                <option value="">Pick a Gear Up response…</option>
                                <option
                                    v-for="response in typeformResponses"
                                    :key="response.responseId"
                                    :value="response.responseId"
                                    :disabled="!!response.sessionId && String(response.sessionId) !== String(sessionId)"
                                >
                                    {{ typeformResponseLabel(response) }}
                                </option>
                            </select>
                            <button
                                type="button"
                                class="btn btn-primary text-nowrap"
                                :disabled="!!busy || !typeformResponseId"
                                @click="linkTypeformResponse"
                            >
                                Bring in
                            </button>
                        </template>
                        <ButtonSpinner v-if="busy === 'upload' || busy === 'typeform'" />
                    </div>
                </div>

                <div v-if="!files.length" class="text-muted small">No files yet. Add a CSV or XLSX to get started.</div>
                <div v-else class="table-responsive">
                    <table class="table table-sm table-hover align-middle mb-0">
                        <thead>
                            <tr>
                                <th>File</th>
                                <th style="width: 10rem;">Target</th>
                                <th>Stage</th>
                                <th class="text-end">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="file in files" :key="file.id">
                                <td>
                                    <div class="fw-semibold">{{ file.sourceFileName || ('File ' + file.id) }}</div>
                                    <div class="small text-muted">#{{ file.id }}</div>
                                </td>
                                <!-- Read-only for now: onboarding builds through BatteryImport. -->
                                <td>{{ jobKindLabel(file.targetJobKind) }}</td>
                                <td>
                                    <span>{{ file.stageName }}</span>
                                    <div v-if="file.precheckPassed === false" class="small text-warning">Precheck failed</div>
                                </td>
                                <td class="text-end">
                                    <div class="d-flex flex-wrap justify-content-end gap-1">
                                        <button
                                            class="btn btn-outline-danger btn-sm"
                                            type="button"
                                            :disabled="!!busy"
                                            @click="removeFile(file)"
                                        >
                                            Remove
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <p v-if="!canAddFile" class="small text-muted mt-2 mb-0">File limit reached ({{ maxFiles }}).</p>
            </div>

            <ul class="nav nav-tabs mb-3">
                <li class="nav-item">
                    <button
                        class="nav-link"
                        :class="{ active: activeTab === 'plan' }"
                        type="button"
                        @click="activeTab = 'plan'"
                    >
                        Build plan
                        <span v-if="openQuestions.length" class="badge bg-warning text-dark ms-1">
                            {{ openQuestions.length }}
                        </span>
                    </button>
                </li>
                <li v-if="!plan" class="nav-item">
                    <button
                        class="nav-link"
                        :class="{ active: activeTab === 'preview' }"
                        type="button"
                        @click="activeTab = 'preview'"
                    >
                        Preview
                        <span v-if="previewErrors.length" class="badge bg-danger ms-1">{{ previewErrors.length }}</span>
                    </button>
                </li>
            </ul>

            <template v-if="activeTab === 'plan'">
                <Spinner v-if="isBuildPlanLoading" />
                <div v-else-if="buildPlanError" class="alert alert-danger">{{ buildPlanError }}</div>
                <template v-else>
                    <div v-if="buildPlan && buildPlan.status === 'Queued'" class="alert alert-info py-2 small">
                        Re-planning from the latest files and messages in the Slack thread…
                    </div>
                    <div v-if="buildPlan && buildPlan.error" class="alert alert-danger py-2 small">
                        The last planning run failed: {{ buildPlan.error }}
                    </div>
                    <div v-if="!plan" class="border rounded p-4 text-center">
                        <p class="mb-1">No build plan yet.</p>
                        <p class="small text-muted mb-0">
                            Drop the customer's workbook in this session's Slack thread; the plan appears here and in the thread.
                        </p>
                    </div>
                    <template v-else>
                        <div class="border rounded p-3 mb-3">
                            <div class="d-flex flex-wrap justify-content-between align-items-start gap-2">
                                <div>
                                    <h2 class="h5 mb-1">{{ buildStage === 'Built' ? 'What this onboarding built' : 'What this onboarding would build' }}</h2>
                                    <p class="small text-muted mb-0">
                                        Planned by {{ buildPlan.model }}
                                        <span v-if="wellDbSummary">· {{ wellDbSummary }}</span>
                                        · sure of it:
                                        <span :class="confidenceClass(plan.confidence)">{{ confidenceLabel(plan.confidence) }}</span>
                                        · corrections go in the Slack thread
                                    </p>
                                </div>
                                <div class="d-flex gap-2 align-items-center">
                                    <span v-if="buildStage === 'Built'" class="badge bg-success">Built</span>
                                    <span v-else-if="isBuilding" class="badge bg-info text-dark">Building…</span>
                                    <button
                                        v-else-if="buildStage === 'NotApproved'"
                                        class="btn btn-success btn-sm"
                                        type="button"
                                        :disabled="!!busy || openQuestions.length > 0 || buildPlan.status === 'Queued' || !session.isOpen"
                                        :title="approveBlockedReason"
                                        @click="approveBuild"
                                    >
                                        <ButtonSpinner v-if="busy === 'approve'" />
                                        Approve and build
                                    </button>
                                    <button
                                        class="btn btn-outline-secondary btn-sm"
                                        type="button"
                                        :disabled="isBuildPlanLoading"
                                        @click="loadBuildPlan"
                                    >
                                        Refresh
                                    </button>
                                </div>
                            </div>
                            <div v-if="approveOutcome && approveOutcome.outcome === 'Refused'" class="alert alert-warning py-2 small mt-2 mb-0">
                                Not built: {{ approveOutcome.refused }}
                            </div>
                            <div v-else-if="approveOutcome && approveOutcome.outcome === 'Replanning'" class="alert alert-info py-2 small mt-2 mb-0">
                                The pre-build check found problems, so the plan is being redone to fix them. Approve the new plan when it arrives.
                                <ul class="mb-0">
                                    <li v-for="(problem, i) in approveOutcome.problems" :key="'p' + i">{{ problem }}</li>
                                </ul>
                            </div>
                            <div v-if="isBuilding" class="alert alert-info py-2 small mt-2 mb-0">
                                Building: the import jobs create batteries, wells, tanks and users, then generic meters,
                                CP types, assignments and groups are added. Refresh to follow it.
                            </div>
                            <div v-if="buildStage === 'Failed'" class="alert alert-danger py-2 small mt-2 mb-0">
                                The build failed: {{ buildPlan.buildLog }}
                            </div>
                            <div v-if="buildResult" class="alert alert-success py-2 small mt-2 mb-0">
                                Built. {{ buildPlan.assetsBuilt }} rows are linked to this plan:
                                {{ buildResult.meters }} generic meters, {{ buildResult.facilityTypesSet }} CP/SWD types, {{ buildResult.collectionPointAssignations }} CP assignations,
                                {{ buildResult.userFacilities }} user facilities, {{ buildResult.groups }} new groups.
                                <div v-if="buildResult.notBuilt.length" class="mt-1">
                                    <strong>Not built:</strong>
                                    <ul class="mb-0"><li v-for="(n, i) in buildResult.notBuilt" :key="'nb' + i">{{ n }}</li></ul>
                                </div>
                                <div v-if="buildResult.byHand.length" class="mt-1">
                                    <strong>Left to do by hand:</strong>
                                    <ul class="mb-0"><li v-for="(n, i) in buildResult.byHand" :key="'bh' + i">{{ n }}</li></ul>
                                </div>
                            </div>
                            <table class="table table-sm small mt-3 mb-0 align-middle">
                                <thead>
                                    <tr class="text-muted">
                                        <th scope="col"></th>
                                        <th scope="col" class="text-end">{{ buildStage === 'Built' ? 'Created' : 'To create' }}</th>
                                        <th scope="col" class="text-end">Already here</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="count in planCounts" :key="count.kind">
                                        <th scope="row" class="fw-normal text-capitalize">{{ count.kind }}</th>
                                        <td class="text-end">{{ count.new }}</td>
                                        <td class="text-end text-muted">{{ count.existing || '—' }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <WorkbookNotices
                            v-if="buildStage === 'NotApproved'"
                            :notes="previewNotes"
                            :problems="previewErrors.concat(previewWarnings)"
                            is-beside-plan
                        />

                        <div
                            v-if="plan.questions.length"
                            class="border rounded p-3 mb-3"
                            :class="{ 'border-warning': openQuestions.length }"
                        >
                            <h2 class="h6">Before this can be built · {{ openQuestions.length }} open</h2>
                            <p class="small text-muted mb-2">
                                Answer them in the Slack thread or the feedback box and the plan is redone with the answers;
                                tick one off when it is resolved or does not apply.
                            </p>
                            <div v-for="(question, i) in plan.questions" :key="'q' + i" class="form-check small">
                                <input
                                    :id="'question-' + i"
                                    class="form-check-input"
                                    type="checkbox"
                                    :checked="(plan.closedQuestions || []).includes(question)"
                                    :disabled="buildStage !== 'NotApproved' || !!busy || !session.isOpen"
                                    @change="setQuestionClosed(question, $event.target.checked)"
                                />
                                <label
                                    class="form-check-label"
                                    :class="{ 'text-decoration-line-through text-muted': (plan.closedQuestions || []).includes(question) }"
                                    :for="'question-' + i"
                                >
                                    {{ question }}
                                </label>
                            </div>
                        </div>

                        <div v-if="buildStage === 'NotApproved'" class="border rounded p-3 mb-3">
                            <h2 class="h6">Feedback for the planner</h2>
                            <p class="small text-muted mb-2">
                                Correct or answer in plain words: "Pecos Bend delivers into Red Bluff", "that row is a well",
                                "Tom's email is tom@…". It goes to the Slack thread too, and the plan is redone with it.
                            </p>
                            <ul v-if="operatorMessages.length" class="list-unstyled small mb-2">
                                <li v-for="message in operatorMessages" :key="message.id" class="border-top py-1">
                                    <span class="badge bg-light text-dark me-1">{{ message.sourceName }}</span>{{ message.body }}
                                </li>
                            </ul>
                            <label class="form-label visually-hidden" for="plan-feedback">Feedback</label>
                            <textarea
                                id="plan-feedback"
                                v-model="feedback"
                                class="form-control form-control-sm mb-2"
                                rows="2"
                                :disabled="!session.isOpen || !!busy"
                                placeholder="What should the plan do differently?"
                            />
                            <button
                                class="btn btn-primary btn-sm"
                                type="button"
                                :disabled="!feedback.trim() || !!busy || !session.isOpen"
                                @click="sendFeedback"
                            >
                                <ButtonSpinner v-if="busy === 'feedback'" />
                                Send and re-plan
                            </button>
                        </div>

                        <div class="border rounded p-3 mb-3">
                            <div class="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
                                <h2 class="h6 mb-0">
                                    Facilities
                                    <span class="text-muted fw-normal">
                                        {{ planQuery ? `${shownFacilities.length} of ${planFacilities.length}` : planFacilities.length }}
                                    </span>
                                </h2>
                                <div class="d-flex flex-wrap align-items-center gap-2">
                                    <input
                                        v-model="planQuery"
                                        class="form-control form-control-sm plan-search"
                                        type="search"
                                        aria-label="Search the plan"
                                        placeholder="Search facilities, wells, tanks, meters, people…"
                                    />
                                    <div class="btn-group btn-group-sm" role="group" aria-label="Facility view">
                                        <button
                                            class="btn"
                                            :class="planStructure === 'list' ? 'btn-secondary' : 'btn-outline-secondary'"
                                            type="button"
                                            @click="planStructure = 'list'"
                                        >
                                            List
                                        </button>
                                        <button
                                            class="btn"
                                            :class="planStructure === 'shell' ? 'btn-secondary' : 'btn-outline-secondary'"
                                            type="button"
                                            @click="planStructure = 'shell'"
                                        >
                                            Shell
                                        </button>
                                    </div>
                                    <button v-if="planStructure === 'list'" class="btn btn-outline-secondary btn-sm" type="button" @click="setAllCollapsed(false)">
                                        Expand all
                                    </button>
                                    <button v-if="planStructure === 'list'" class="btn btn-outline-secondary btn-sm" type="button" @click="setAllCollapsed(true)">
                                        Collapse all
                                    </button>
                                </div>
                            </div>

                            <p v-if="planQuery && !shownFacilities.length" class="small text-muted mb-0">
                                Nothing in the facilities matches “{{ planQuery }}”.
                            </p>

                            <PlanShell
                                v-if="planStructure === 'shell'"
                                :facilities="planFacilities"
                                :people="plan.people || []"
                                :groups="plan.groups || []"
                                :query="planQuery"
                            />

                            <div v-for="facility in shownFacilities" v-else :key="facility.name" class="plan-facility border rounded mb-2">
                                <div class="plan-facility-head d-flex flex-wrap align-items-center gap-2 px-3 py-2">
                                    <button
                                        class="btn btn-link btn-sm p-0 text-decoration-none text-body d-flex align-items-center gap-1"
                                        type="button"
                                        :aria-expanded="!isFacilityCollapsed(facility)"
                                        :title="facility.source"
                                        @click="toggleFacility(facility)"
                                    >
                                        <i
                                            class="dx-icon"
                                            :class="isFacilityCollapsed(facility) ? 'dx-icon-chevronright' : 'dx-icon-chevrondown'"
                                            aria-hidden="true"
                                        />
                                        <span class="fw-semibold">{{ facility.name }}</span>
                                    </button>
                                    <span class="badge rounded-pill fw-normal" :class="statusBadgeClass(facility.status)">{{ facility.status }}</span>
                                    <span v-if="facilityTypeLabel(facility.type)" class="badge rounded-pill fw-normal text-bg-info">
                                        {{ facilityTypeLabel(facility.type) }}
                                    </span>
                                    <span v-if="facility.collectionPoint" class="small text-muted">&rarr; {{ facility.collectionPoint }}</span>
                                    <span class="ms-auto small text-muted">
                                        {{ facility.wells.length }} wells · {{ facility.tanks.length }} tanks · {{ facility.meters.length }} meters
                                        <template v-if="facility.county || facility.state">
                                            · {{ [facility.county, facility.state].filter(Boolean).join(', ') }}
                                        </template>
                                    </span>
                                </div>

                                <div v-if="!isFacilityCollapsed(facility)" class="row g-0 border-top">
                                    <div
                                        v-for="column in [
                                            { label: 'Wells', items: facility.wells, kind: 'well' },
                                            { label: 'Tanks', items: facility.tanks, kind: 'tank' },
                                            { label: 'Meters', items: facility.meters, kind: 'meter' }
                                        ]"
                                        :key="column.label"
                                        class="col-md-4 px-3 py-2 plan-column"
                                    >
                                        <div class="plan-column-title">{{ column.label }} <span>{{ column.items.length }}</span></div>
                                        <div v-if="!column.items.length" class="small text-muted">None</div>
                                        <div v-for="item in column.items" :key="item.name" class="plan-item">
                                            <div class="d-flex align-items-start gap-2">
                                                <span class="badge rounded-pill fw-normal mt-1" :class="statusBadgeClass(item.status)">{{ item.status }}</span>
                                                <div class="min-w-0">
                                                    <div class="plan-item-name">{{ item.name }}</div>
                                                    <div class="small text-muted">
                                                        <template v-if="column.kind === 'well'">
                                                            {{ item.apiNumber ? `API ${item.apiNumber}` : 'No API number' }}
                                                            <template v-if="item.field || item.county">
                                                                · {{ [item.field, item.county].filter(Boolean).join(', ') }}
                                                            </template>
                                                            <template v-if="item.latitude != null && item.longitude != null">
                                                                ·
                                                                <a
                                                                    :href="`https://www.google.com/maps?q=${item.latitude},${item.longitude}`"
                                                                    target="_blank"
                                                                    rel="noopener"
                                                                >{{ item.latitude.toFixed(4) }}, {{ item.longitude.toFixed(4) }}</a>
                                                            </template>
                                                        </template>
                                                        <template v-else-if="column.kind === 'tank'">
                                                            {{ item.product }} ·
                                                            <span v-if="item.heightInches == null" class="text-warning">height missing</span>
                                                            <template v-else>{{ feet(item.heightInches) }}</template>
                                                            ·
                                                            <span v-if="item.barrelsPerInch == null" class="text-warning">bbl/in missing</span>
                                                            <template v-else>{{ item.barrelsPerInch }} bbl/in</template>
                                                        </template>
                                                        <template v-else>
                                                            <span class="text-capitalize">{{ item.kind }}</span><template v-if="item.type"> · {{ item.type }}</template>
                                                        </template>
                                                    </div>
                                                    <div v-if="column.kind === 'well' && item.wellDb" class="plan-reference">
                                                        Well database:
                                                        {{ [item.wellDb.operator, item.wellDb.status, item.wellDb.type].filter(Boolean).join(' · ') }}
                                                        <template v-if="item.filledFromWellDb && item.filledFromWellDb.length">
                                                            · filled in {{ item.filledFromWellDb.join(', ') }}
                                                        </template>
                                                    </div>
                                                    <div v-else-if="column.kind === 'well' && item.apiNumber" class="small text-warning">
                                                        Not in the well database
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="row g-3 mb-4">
                            <div class="col-lg-8">
                                <div class="border rounded p-3 h-100">
                                    <div class="d-flex justify-content-between align-items-baseline">
                                        <h2 class="h6">People</h2>
                                        <button
                                            v-if="plan.people.some(p => p.bookie) && buildStage === 'NotApproved'"
                                            class="btn btn-link btn-sm p-0"
                                            type="button"
                                            :disabled="!!busy"
                                            @click="setBookie(null)"
                                        >
                                            No bookie
                                        </button>
                                    </div>
                                    <table class="table table-sm small mb-0 align-middle">
                                        <thead>
                                            <tr class="text-muted">
                                                <th scope="col"></th>
                                                <th scope="col">Name</th>
                                                <th scope="col">Role</th>
                                                <th scope="col">Email</th>
                                                <th scope="col">Facilities</th>
                                                <th scope="col" class="text-center" title="The company's bookie: alerts escalate to them. One person.">Bookie</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr v-for="person in shownPeople" :key="person.name">
                                                <td><span class="badge" :class="statusBadgeClass(person.status)">{{ person.status }}</span></td>
                                                <td>{{ person.name }}</td>
                                                <td>{{ person.role }}</td>
                                                <td :class="{ 'text-danger': !person.email }">{{ person.email || 'no email' }}</td>
                                                <td class="text-muted" :title="person.facilities.join(', ')">
                                                    {{ person.facilities.length > 3 ? `${person.facilities.length} facilities` : (person.facilities.join(', ') || '—') }}
                                                </td>
                                                <td class="text-center">
                                                    <input
                                                        class="form-check-input"
                                                        type="radio"
                                                        name="plan-bookie"
                                                        :aria-label="`${person.name} is the bookie`"
                                                        :checked="person.bookie"
                                                        :disabled="buildStage !== 'NotApproved' || !!busy || !session.isOpen"
                                                        @change="setBookie(person)"
                                                    />
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                            <div class="col-lg-4">
                                <div class="border rounded p-3 h-100">
                                    <h2 class="h6">Facility groups</h2>
                                    <div v-if="!plan.groups.length" class="small text-muted">None in this workbook</div>
                                    <div v-for="group in shownGroups" :key="group.name" class="border-top py-2 small">
                                        <span class="badge me-1" :class="statusBadgeClass(group.status)">{{ group.status }}</span>
                                        <strong>{{ group.name }}</strong>
                                        <div class="text-muted">{{ group.facilities.join(', ') }}</div>
                                        <div v-if="group.members.length">Members: {{ group.members.join(', ') }}</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </template>
                </template>
            </template>

            <template v-if="activeTab === 'preview'">
                <Spinner v-if="isPreviewLoading" />
                <div v-else-if="previewError" class="alert alert-danger">{{ previewError }}</div>
                <div v-else-if="!hasExtraction" class="border rounded p-4 text-center">
                    <p class="mb-1">This session has not read a workbook yet.</p>
                    <p v-if="!files.length" class="small text-muted mb-0">
                        Add a CSV or XLSX above, or drop it in the session's Slack thread.
                    </p>
                    <p v-else class="small text-muted mb-0">
                        The files are still being read; refresh in a moment.
                    </p>
                </div>
                <template v-else>
                    <div class="border rounded p-3 mb-3">
                        <div class="d-flex flex-wrap justify-content-between align-items-start gap-2">
                            <div>
                                <h2 class="h5 mb-1">What this workbook would create</h2>
                                <p class="small text-muted mb-0">
                                    {{ previewCounts.facilities }} facilities,
                                    {{ previewCounts.wells }} wells,
                                    {{ previewCounts.tanks }} tanks,
                                    {{ previewCounts.meters }} water meters and
                                    {{ previewCounts.pumperAssignments }} pumper assignments
                                </p>
                            </div>
                            <div class="d-flex flex-wrap gap-2 align-items-center">
                                <span class="badge bg-success" title="Not in this company yet — the import would create these">
                                    {{ previewCounts.new }} to create
                                </span>
                                <span class="badge bg-secondary" title="Already in this company — the import would not duplicate these">
                                    {{ previewCounts.existing }} already here
                                </span>
                                <span
                                    v-if="previewCounts.missing"
                                    class="badge bg-danger"
                                    title="Named by the workbook but absent from this company, and not created by the import"
                                >
                                    {{ previewCounts.missing }} won't resolve
                                </span>
                                <button
                                    class="btn btn-outline-secondary btn-sm"
                                    type="button"
                                    :disabled="!!busy"
                                    @click="loadPreview"
                                >
                                    Refresh
                                </button>
                            </div>
                        </div>

                        <table class="table table-sm small mt-3 mb-0 align-middle">
                            <thead>
                                <tr class="text-muted">
                                    <th scope="col">Section</th>
                                    <th scope="col" class="text-end">Rows</th>
                                    <th scope="col" class="text-end">Sure of what it read</th>
                                    <th scope="col" class="text-end">Columns it could read</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="section in previewSections" :key="section.kind + '-' + section.fileId">
                                    <th scope="row" class="fw-normal">{{ section.kindName }}</th>
                                    <td class="text-end">{{ section.rowCount }}</td>
                                    <td class="text-end" :class="confidenceClass(section.confidence)">
                                        {{ confidenceLabel(section.confidence) }}
                                    </td>
                                    <td class="text-end" :class="confidenceClass(section.coverage)">
                                        {{ confidenceLabel(section.coverage) }}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <WorkbookNotices :notes="previewNotes" :problems="previewErrors.concat(previewWarnings)" />

                    <div class="border rounded p-3 mb-4">
                        <h2 class="h6">Facilities</h2>
                        <div v-for="facility in previewFacilities" :key="facilityKey(facility)" class="border-top py-2">
                            <div class="d-flex flex-wrap align-items-center gap-2">
                                <button
                                    class="btn btn-link btn-sm p-0 text-decoration-none d-flex align-items-center gap-1"
                                    type="button"
                                    :aria-expanded="!isFacilityCollapsed(facility)"
                                    @click="toggleFacility(facility)"
                                >
                                    <i
                                        class="dx-icon"
                                        :class="isFacilityCollapsed(facility) ? 'dx-icon-chevronright' : 'dx-icon-chevrondown'"
                                        aria-hidden="true"
                                    />
                                    <strong>{{ facility.name || 'Rows with no facility' }}</strong>
                                </button>
                                <span class="badge" :class="statusBadgeClass(facility.status)">{{ facility.status }}</span>
                                <span class="small text-muted">
                                    {{ facility.wells.length }} wells,
                                    {{ facility.tanks.length }} tanks,
                                    {{ facility.meters.length }} meters,
                                    {{ facility.pumpers.length }} pumpers
                                </span>
                                <span v-if="facility.sheet" class="small text-muted">· {{ facility.sheet }}</span>
                            </div>

                            <div v-if="!isFacilityCollapsed(facility)" class="row g-3 mt-1 ms-3">
                                <div
                                    v-for="group in [
                                        { label: 'Wells', nodes: facility.wells },
                                        { label: 'Tanks', nodes: facility.tanks },
                                        { label: 'Meters', nodes: facility.meters },
                                        { label: 'Pumpers', nodes: facility.pumpers }
                                    ]"
                                    :key="group.label"
                                    class="col-md-3"
                                >
                                    <div class="small fw-semibold">{{ group.label }} ({{ group.nodes.length }})</div>
                                    <div v-if="!group.nodes.length" class="small text-muted">None in this workbook</div>
                                    <ul v-else class="list-unstyled mb-0 small">
                                        <li
                                            v-for="node in group.nodes"
                                            :key="[group.label, node.sheet, node.rowNumber, node.name].join('|')"
                                        >
                                            <span class="badge me-1" :class="statusBadgeClass(node.status)">
                                                {{ node.status }}
                                            </span>
                                            {{ node.name }}
                                            <span v-if="node.detail" class="text-muted">— {{ node.detail }}</span>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </template>
            </template>
        </template>
    </div>
</template>

<style lang="scss" scoped>
    .plan-search {
        width: 22rem;
        max-width: 100%;
    }

    .plan-facility-head {
        background: var(--bs-tertiary-bg);
    }

    .plan-column + .plan-column {
        border-left: 1px solid var(--bs-border-color);
    }

    .plan-column-title {
        font-size: 0.75rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        color: var(--bs-secondary-color);
        margin-bottom: 0.25rem;

        span {
            font-weight: 400;
        }
    }

    .plan-item {
        padding: 0.375rem 0;

        & + & {
            border-top: 1px dashed var(--bs-border-color);
        }
    }

    .plan-item-name {
        font-size: 0.875rem;
        line-height: 1.3;
    }

    .plan-reference {
        font-size: 0.75rem;
        color: var(--bs-secondary-color);
        opacity: 0.8;
    }

    .min-w-0 {
        min-width: 0;
    }

    @media (max-width: 767.98px) {
        .plan-column + .plan-column {
            border-left: 0;
            border-top: 1px solid var(--bs-border-color);
        }
    }
</style>
